import type React from 'react';
import type { PropsWithChildren } from 'react';
import { useMemo } from 'react';

import { useI18n } from '@change-corgi/core/react/i18n';
import { TrackingContext } from '@change-corgi/core/react/tracking';
import type { EmptyProps } from '@change-corgi/core/types';

import { useQueryParamValue } from 'src/app/shared/hooks/location';
import { useSession } from 'src/app/shared/hooks/session';
import { convertToUsdString } from 'src/app/shared/utils/currency';
import { calculateImpressions } from 'src/app/shared/utils/promote';

import { useAvailablePaymentMethods } from './availablePaymentMethodsContext';
import { usePromoteFormData } from './formData';
import { usePromoteFormState } from './formState';

export const PromoteFormTrackingContextProvider: React.FC<PropsWithChildren<EmptyProps>> = ({ children }) => {
	const [{ amount, isPublic, paymentMethodSaveOptions }] = usePromoteFormState();
	const { amountChoices, currency, petition, promotionSettings, user } = usePromoteFormData();

	const { paymentMethods } = useAvailablePaymentMethods();

	const { amountToBaseUnits } = useI18n();
	const session = useSession();
	const sourceLocation = useQueryParamValue('source_location');
	const currentSource = useQueryParamValue('utm_source') || session.trackingData.current_source;

	const amountInBaseUnits = useMemo(
		() => amountToBaseUnits(amount, currency.code),
		[amountToBaseUnits, amount, currency.code],
	);

	const bonusImpressions = useMemo(
		() => calculateImpressions(amountInBaseUnits, promotionSettings.cpm),
		[amountInBaseUnits, promotionSettings.cpm],
	);

	const impressionsToFulfill = useMemo(
		() =>
			calculateImpressions(
				amountInBaseUnits,
				promotionSettings.cpm,
				promotionSettings.bonusCpm,
				promotionSettings.bonusThreshold,
			),
		[amountInBaseUnits, promotionSettings.bonusCpm, promotionSettings.bonusThreshold, promotionSettings.cpm],
	);

	return (
		<TrackingContext
			properties={{
				amount: convertToUsdString(amount, currency.exchangeRate),
				bonus_cpm: promotionSettings.bonusCpm ?? undefined,
				bonus_impressions: bonusImpressions,
				cpm: promotionSettings.cpm,
				currency: currency.code,
				custom_amount: !amountChoices.map((choice) => choice.amount).includes(amount),
				exchange_rate: currency.exchangeRate,
				impressions_to_fulfill: impressionsToFulfill,
				local_amount: amount,
				own_petition: petition.user.id === user.id,
				petition_id: `petition_${petition.id}`,
				price: Number(convertToUsdString(amount, currency.exchangeRate)),
				product_flow: 'promoted',
				promote_transaction: 'single',
				public: isPublic,
				quantity: 1,
				revenue_type: 'promotion',
				save_payment_method: paymentMethodSaveOptions.shouldSavePaymentMethod,
				source_location: sourceLocation,
				use_saved_payment_method: false,
				visible_payment_types: paymentMethods,
				context: 'promote_or_share',
			}}
			defaultOverrides={{ current_source: currentSource }}
		>
			{children}
		</TrackingContext>
	);
};
